import { useEffect, useRef, useState } from "react";

import {
	Space,
	Table,
	ConfigProvider,
	Form,
	Button,
	Pagination,
	Empty,
} from "antd";

import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import PropTypes from "prop-types";
import { createStyles } from "antd-style";
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 *
 * @param {Object} props
 * @param {import('antd').FormInstance} props.form - Ant Design form instance from parent.
 * @param {function(string): void} props.setEditingKey - Setter for editing key state.
 * @param {boolean} props.reloadTable - Flag to trigger table reload.
 * @param {function(boolean): void} props.setReloadTable - Function to toggle reloadTable state.
 * @param {Array<Object>} props.tableEntries - Array of data entries for the table.
 * @param {Array<{
 *   title: string,
 *   dataIndex: string,
 *   key: string,
 *   align: "left" | "right" | "center",
 *   sortOrder?: number | null,
 *   sorter?: ((a: any, b: any) => number) | null,
 *   filters?: Array<{ text: string; value: string | number | boolean }>,
 *   filterSearch?: boolean,
 *   filteredValue?: string | number | boolean | null,
 *   onFilter?: (value: any, record: any) => boolean,
 *   editable?: boolean,
 *   render?: (text: any, record: any, index?: number) => JSX.Element
 * }>} props.tableColumns - Array of column definitions for the table.
 * @param {boolean} props.loading - Loading state for the table.
 * @param {Array<Object>} [props.expandedTableEntries=[]] - Data entries for the expanded rows. Defaults to empty array.
 * @param {Array<{
 *   title: string,
 *   dataIndex: string,
 *   key: string,
 *   align: "left" | "right" | "center",
 *   sortOrder?: number | null,
 *   sorter?: ((a: any, b: any) => number) | null,
 *   filters?: Array<{ text: string; value: string | number | boolean }>,
 *   filterSearch?: boolean,
 *   filteredValue?: string | number | boolean | null,
 *   onFilter?: (value: any, record: any) => boolean,
 *   editable?: boolean,
 *   render?: (text: any, record: any, index?: number) => JSX.Element
 * }>} [props.expandedTableColumns=[]] - Column definitions for the expanded rows. Defaults to empty array.
 * @param {(string|number)[]} [props.expandedParentRowKeys=[]] - Keys of rows expanded in the **outer table** when editing. Should contain at most one key. Defaults to empty array.
 * @param {function((string|number)[]): void} props.setExpandedParentRowKeys
 * @param {(string | ((record: object) => string | number))} [props.expandedRowTableKey] - The `rowKey` for the inner expanded table. This is a prop injected in the table column.
 * @param {(itemKey?: string | number | null) => void} props.cancel - Function to cancel editing.
 *   If an `itemKey` is provided, it may remove a blank row.
 *   This function is also used as the `onChange` handler for pagination,
 *   so changing the page or page size automatically cancels any active edits.
 * @param {JSX.Element} props.contextHolder - Context holder for Ant Design messages/notifications.
 * @param {function(
 *   import('antd/es/table/interface').SorterResult<any> |
 *   import('antd/es/table/interface').SorterResult<any>[]
 * ): void} props.setSortedInfo
 * @param {function(Record<string, (string|number|boolean)[] | null> ): void} props.setFilteredInfo
 * @param {function(Object): JSX.Element} props.EditableCell - Component used to render editable table cells.
 *   Receives props:
 *     - `editing` (boolean) - Whether the cell is being edited.
 *     - `dataIndex` (string) - Column dataIndex.
 *     - `title` (string) - Column title.
 *     - `record` (Object) - Row data.
 *     - `children` (React.ReactNode) - Default cell content.
 *     - `isExpandedTable` (boolean, optional) - Whether the cell is inside an expanded table.
 *     - `...restProps` - Any additional props passed down from Table.
 * @param {function(Object): boolean} props.isEditing
 * @param {string} props.tableTitle - Title displayed at the top of the table.
 * @param {string} [addBtnText]
 * @param {function():void} [addBtnFunction]
 * @returns {JSX.Element} The rendered table component.
 */
export default function CustomTable({
	form,
	setEditingKey,
	reloadTable,
	setReloadTable,
	tableEntries,
	tableColumns,
	loading,
	expandedTableEntries = [],
	expandedTableColumns = [],
	expandedParentRowKeys = [],
	expandedRowTableKey,
	setExpandedParentRowKeys,
	cancel,
	contextHolder,
	setSortedInfo,
	setFilteredInfo,
	EditableCell,
	isEditing,
	tableTitle,
	addBtnText,
	addBtnFunction,
}) {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const headerRef = useRef(null);
	const tableRef = useRef(null);
	const [tableHeight, setTableHeight] = useState(0);
	const [totalRecords, setTotalRecords] = useState(0);
	const useStyle = createStyles(({ css, token }) => {
		const { antCls } = token;
		return {
			customTable: css`
				${antCls}-table {
					${antCls}-table-container {
						${antCls}-table-body,
						${antCls}-table-content {
							scrollbar-width: thin;
							scrollbar-color: #eaeaea transparent;
							scrollbar-gutter: stable;
						}
					}
				}
			`,
		};
	});
	const { styles } = useStyle();
	const updateHeight = () => {
		if (headerRef.current && tableRef.current) {
			const headerBottom = headerRef.current.getBoundingClientRect().bottom;
			const tableContentHeight = tableRef.current.scrollHeight; // includes expanded rows
			const buffer = 60;

			const maxAvailableHeight = window.innerHeight - headerBottom - buffer;

			// Only constrain if table content exceeds maxAvailableHeight
			setTableHeight(
				tableContentHeight > maxAvailableHeight ? maxAvailableHeight : null
			);
		}
	};

	// Run on mount, window resize, and after relevant actions
	useEffect(() => {
		updateHeight();
		window.addEventListener("resize", updateHeight);
		return () => window.removeEventListener("resize", updateHeight);
	}, [tableEntries, expandedParentRowKeys, pageSize, currentPage]);
	/**
	 * Handler for Ant Design Table changes (pagination, filters, sorter, etc.).
	 *
	 * @param {import('antd/es/table/interface').TablePaginationConfig} pagination - Pagination info.
	 * @param {Record<string, (string|number|boolean)[] | null>} filters - Column filters.
	 * @param {import('antd/es/table/interface').SorterResult<any> | import('antd/es/table/interface').SorterResult<any>[]} sorter - Sorting info.
	 * @param {{ currentDataSource: any[]; action: 'paginate' | 'sort' | 'filter' }} extra - Extra info about the change.
	 */
	const tableOnChange = (pagination, filters, sorter, extra) => {
		setFilteredInfo(filters); // store the filters in state
		setSortedInfo(sorter);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		console.log("params", pagination, filters, sorter, extra);
	};

	const makeEditableColumns = (columns, isExpandedTable = false) =>
		columns.map((col) => {
			return {
				...col,
				onCell: (record) => ({
					record,
					dataIndex: col.dataIndex,
					title: col.title,
					editing: isEditing(record),
					isExpandedTable,
					editable: col.editable || false,
				}),
			};
		});

	const expandedRowRender = (parentRow) => {
		const rowData = expandedTableEntries.filter((e) => e.key === parentRow.key);
		if (!rowData.length) return null;

		return (
			<Table
				columns={makeEditableColumns(expandedTableColumns, true)}
				dataSource={rowData}
				pagination={false}
				scroll={{ x: "10" }}
				rowKey={expandedRowTableKey}
				components={{ body: { cell: EditableCell } }}
				size="small"
			/>
		);
	};
	return (
		<>
			{contextHolder}
			<ConfigProvider
				theme={{
					components: {
						Table: {
							headerBg: "#232323",
							headerColor: "#FFF",
							borderColor: "#868686",
							headerSplitColor: "#868686",
						},
						Button: {
							defaultColor: "#000",
							defaultHoverBorderColor: "transparent",
							defaultBorderColor: "transparent",
							defaultHoverColor: "#000",
							borderRadius: "24px",
							primaryColor: "#232323", //Color of text
							primaryShadow: "none",
							colorLink: "#bcff00",
						},
						Checkbox: {
							colorPrimary: "#bcff00", // sets checkmark & border color
						},
					},
					token: {
						colorBgContainer: "#3C3C3C",
						colorText: "#FFF",
						colorPrimary: "#bcff00",
						colorTextDisabled: "#969696",
						colorTextQuaternary: "#969696",
						colorTextTertiary: "#868686",
						controlItemBgHover: "#868686",
						controlItemBgActive: "#565656",
						controlItemBgActiveHover: "#868686",
					},
				}}
			>
				<Form form={form} component={false}>
					<div ref={headerRef} style={{ width: "100%" }}>
						<div
							style={{
								position: "sticky",
								top: 0,
								background: "#131313",
								paddingInline: "10px",
								paddingBlock: "2px",
								zIndex: 10,
								width: "100%",
								display: "inline-flex",
								justifyContent: "space-between",
							}}
						>
							<div
								style={{
									background: "#131313",
									display: "flex",
									justifyContent: "start", // Aligns items horizontally to the start
									alignItems: "center",
								}}
							>
								<h3 style={{ color: "white" }}>{tableTitle}</h3>
							</div>

							<div
								style={{
									background: "#131313",
									display: "flex",
									justifyContent: "end", // Aligns items horizontally to the start
									alignItems: "center",
								}}
							>
								<ConfigProvider
									theme={{
										components: {
											Select: {
												optionSelectedColor: "#262626",
												optionSelectedBg: "#bcff00",
											},
										},
										token: {
											colorBgElevated: "#262626",
											controlItemBgHover: "#868686",
										},
									}}
								>
									<Space size="middle">
										<ConfigProvider
											theme={{
												components: {
													Button: {
														defaultHoverBorderColor: "#bcff00",
														defaultHoverColor: "#bcff00", //for text
														borderRadius: "10px",
														primaryColor: "#bcff00", //Color of text
														primaryShadow: "none",
													},
												},
												token: {
													colorBgContainer: "#fff",
												},
											}}
										>
											<Space size="small">
												{addBtnText && (
													<ConfigProvider
														theme={{
															components: {
																Button: {
																	defaultHoverBg: "#868686",
																	defaultHoverBorderColor: "#868686",
																},
															},
															token: {
																colorBgContainer: "#bcff00",
															},
														}}
													>
														<Button
															style={{ marginRight: "10px" }}
															onClick={addBtnFunction}
														>
															{addBtnText}
														</Button>
													</ConfigProvider>
												)}
												<Button
													ghost
													onClick={() => {
														setFilteredInfo({});
														setSortedInfo([]);
													}}
												>
													Clear Filter and Sorting
												</Button>
												<Button
													ghost
													style={{ borderRadius: "50px" }}
													onClick={() => {
														setEditingKey("");
														setReloadTable(!reloadTable);
													}}
													icon={<ReloadOutlined />}
												/>
											</Space>
										</ConfigProvider>

										<Pagination
											current={currentPage}
											pageSize={pageSize}
											total={totalRecords}
											showSizeChanger
											pageSizeOptions={[10, 20, 30, 40, 100]}
											showTotal={(total) => `Total ${total} items`}
											onChange={(page, size) => {
												setCurrentPage(page);
												setPageSize(size);
											}}
											responsive
											fixed="top"
											hideOnSinglePage={true}
										/>
									</Space>
								</ConfigProvider>
							</div>
						</div>
					</div>
					<Table
						ref={tableRef}
						components={{ body: { cell: EditableCell } }}
						loading={loading}
						className={styles.customTable}
						style={{
							paddingInline: "10px",
						}}
						expandable={
							expandedTableEntries?.length > 0
								? {
										expandedRowRender,
										expandedRowKeys: expandedParentRowKeys,
										onExpand: (expanded, record) => {
											setExpandedParentRowKeys(expanded ? [record.key] : []);
										},
								  }
								: undefined
						}
						scroll={{ y: tableHeight, x: "max-content" }}
						bordered={false}
						columns={makeEditableColumns(tableColumns)}
						dataSource={tableEntries}
						onChange={tableOnChange}
						rowClassName="editable-row"
						size="small"
						sticky={true}
						pagination={{
							position: ["topRight"],
							current: currentPage,
							pageSize: pageSize,
							showSizeChanger: true,
							pageSizeOptions: [10, 20, 30, 40, 100],
							showTotal: (total) => setTotalRecords(total),
							onChange: cancel,
							style: { display: "none" },
						}}
						locale={{
							emptyText: (
								<Empty
									description={
										<div style={{ color: "#fff" }}>No data available</div>
									}
								/>
							),
						}}
					/>
				</Form>
			</ConfigProvider>
		</>
	);
}

CustomTable.propTypes = {
	form: PropTypes.object.isRequired,
	setEditingKey: PropTypes.func.isRequired,
	reloadTable: PropTypes.bool.isRequired,
	setReloadTable: PropTypes.func.isRequired,
	tableEntries: PropTypes.arrayOf(PropTypes.object).isRequired,
	tableColumns: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			dataIndex: PropTypes.string.isRequired,
			key: PropTypes.string.isRequired,
			align: PropTypes.oneOf(["left", "right", "center"]).isRequired,
			sortOrder: PropTypes.oneOfType([
				PropTypes.number,
				PropTypes.oneOf([null]),
			]),
			sorter: PropTypes.func,
			filters: PropTypes.arrayOf(
				PropTypes.shape({
					text: PropTypes.string.isRequired,
					value: PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.number,
						PropTypes.bool,
					]).isRequired,
				})
			),
			filterSearch: PropTypes.bool,
			filteredValue: PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.number,
				PropTypes.bool,
			]),
			onFilter: PropTypes.func,
			editable: PropTypes.bool,
			render: PropTypes.func,
		})
	).isRequired,
	loading: PropTypes.bool.isRequired,
	expandedTableEntries: PropTypes.arrayOf(PropTypes.object),
	expandedTableColumns: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			dataIndex: PropTypes.string.isRequired,
			key: PropTypes.string.isRequired,
			align: PropTypes.oneOf(["left", "right", "center"]).isRequired,
			sortOrder: PropTypes.oneOfType([
				PropTypes.number,
				PropTypes.oneOf([null]),
			]),
			sorter: PropTypes.func,
			filters: PropTypes.arrayOf(
				PropTypes.shape({
					text: PropTypes.string.isRequired,
					value: PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.number,
						PropTypes.bool,
					]).isRequired,
				})
			),
			filterSearch: PropTypes.bool,
			filteredValue: PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.number,
				PropTypes.bool,
			]),
			onFilter: PropTypes.func,
			editable: PropTypes.bool,
			render: PropTypes.func,
		})
	),
	expandedParentRowKeys: (props, propName, componentName) => {
		const value = props[propName];
		if (value === undefined) return null;
		if (!Array.isArray(value)) return new Error(`${propName} must be an array`);
		if (value.length > 1)
			return new Error(`${propName} can contain at most 1 key`);
		return null;
	},
	setExpandedParentRowKeys: PropTypes.func.isRequired,
	expandedRowTableKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	cancel: PropTypes.func.isRequired,
	contextHolder: PropTypes.element.isRequired,
	setSortedInfo: PropTypes.func.isRequired,
	setFilteredInfo: PropTypes.func.isRequired,
	EditableCell: PropTypes.elementType.isRequired,
	isEditing: PropTypes.func.isRequired,
	tableTitle: PropTypes.string.isRequired,
	addBtnText: PropTypes.string,
	addBtnFunction: PropTypes.func,
};
CustomTable.defaultProps = {
	expandedTableEntries: [],
	expandedTableColumns: [],
	expandedRowTableKey: [],
	addBtnText: null,
	addBtnFunction: () => {},
};
